import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TrialBalance.css';
import baseURL from '../../../../Url/NodeBaseURL';

const TrialBalance = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trialBalanceData, setTrialBalanceData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: ''
  });

  // Fetch Trial Balance data
  const fetchTrialBalance = async () => {
    if (!dateRange.start_date || !dateRange.end_date) {
      alert('Please select both start and end dates');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${baseURL}/ledger/trial-balance`, {
        params: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date
        }
      });

      console.log('Trial Balance Response:', response.data);

      if (response.data.success) {
        setTrialBalanceData(response.data);
      } else {
        setError(response.data.message || 'Failed to fetch Trial Balance data');
      }
    } catch (err) {
      console.error('Error fetching Trial Balance:', err);
      setError(err.response?.data?.error || 'Failed to fetch Trial Balance data');
    } finally {
      setLoading(false);
    }
  };

  // Handle date change
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '₹0.00';
    return `₹${Number(amount).toFixed(2)}`;
  };

  // Set default date range to current financial year
  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    let startYear = year;
    if (month < 3) {
      startYear = year - 1;
    }
    
    setDateRange({
      start_date: `${startYear}-04-01`,
      end_date: today.toISOString().split('T')[0]
    });
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    if (dateRange.start_date && dateRange.end_date) {
      fetchTrialBalance();
    }
  }, []);

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="tb-container">
      <div className="tb-header">
        <div className="tb-header-top">
          <h2>📒 Trial Balance</h2>
          <div className="tb-header-actions">
            <button onClick={fetchTrialBalance} className="btn-refresh" disabled={loading}>
              🔄 Refresh
            </button>
            <button onClick={handlePrint} className="btn-print">
              🖨️ Print
            </button>
          </div>
        </div>
        <div className="tb-controls">
          <div className="date-range">
            <div className="date-group">
              <label>From:</label>
              <input
                type="date"
                name="start_date"
                value={dateRange.start_date}
                onChange={handleDateChange}
                className="date-input"
              />
            </div>
            <div className="date-group">
              <label>To:</label>
              <input
                type="date"
                name="end_date"
                value={dateRange.end_date}
                onChange={handleDateChange}
                className="date-input"
              />
            </div>
            <button 
              onClick={fetchTrialBalance}
              className="btn-generate"
              disabled={loading}
            >
              {loading ? '⏳ Loading...' : '📊 Generate'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="tb-error">
          <span className="error-icon">❌</span>
          {error}
        </div>
      )}

      {loading ? (
        <div className="tb-loading">
          <div className="loader"></div>
          <p>Generating Trial Balance...</p>
        </div>
      ) : trialBalanceData ? (
        <div className="tb-content" id="print-area">
          <div className="tb-report-header">
            <h3>Trial Balance</h3>
            <p className="tb-period">
              For the period from {new Date(trialBalanceData.period.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} 
              to {new Date(trialBalanceData.period.end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
          </div>

          <div className="tb-table-wrapper">
            <table className="tb-table">
              <thead>
                <tr>
                  <th className="col-sno">#</th>
                  <th className="col-account">Account Name</th>
                  <th className="col-debit text-right">Debit (Dr)</th>
                  <th className="col-credit text-right">Credit (Cr)</th>
                  <th className="col-balance text-right">Balance</th>
                  <th className="col-count text-center">Transactions</th>
                </tr>
              </thead>
              <tbody>
                {trialBalanceData.accounts && trialBalanceData.accounts.length > 0 ? (
                  trialBalanceData.accounts.map((account, index) => {
                    const balanceType = account.balance >= 0 ? 'Dr' : 'Cr';
                    return (
                      <tr key={index} className={account.balance !== 0 ? 'has-balance' : ''}>
                        <td className="text-center">{index + 1}</td>
                        <td className="account-name">{account.account_name}</td>
                        <td className="text-right debit-amount">
                          {account.debit > 0 ? formatCurrency(account.debit) : '-'}
                        </td>
                        <td className="text-right credit-amount">
                          {account.credit > 0 ? formatCurrency(account.credit) : '-'}
                        </td>
                        <td className={`text-right balance-amount ${balanceType === 'Dr' ? 'balance-dr' : 'balance-cr'}`}>
                          {account.balance !== 0 ? (
                            <>
                              {formatCurrency(Math.abs(account.balance))}
                              <span className="balance-type">{balanceType}</span>
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="text-center">{account.transaction_count}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center no-data">No transactions found</td>
                  </tr>
                )}
                <tr className="total-row">
                  <td colSpan="2" className="total-label"><strong>Total</strong></td>
                  <td className="text-right total-debit"><strong>{formatCurrency(trialBalanceData.totals.total_debit)}</strong></td>
                  <td className="text-right total-credit"><strong>{formatCurrency(trialBalanceData.totals.total_credit)}</strong></td>
                  <td colSpan="2"></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className={`tb-verification ${trialBalanceData.totals.is_balanced ? 'balanced' : 'unbalanced'}`}>
            {trialBalanceData.totals.is_balanced ? (
              <>
                <span className="verification-icon">✅</span>
                <span>Trial Balance is Balanced</span>
                <span className="verification-detail">(Total Debit = Total Credit)</span>
              </>
            ) : (
              <>
                <span className="verification-icon">⚠️</span>
                <span>Trial Balance is NOT Balanced</span>
                <span className="verification-detail">Difference: {formatCurrency(Math.abs(trialBalanceData.totals.difference))}</span>
              </>
            )}
          </div>

          {/* Quick Stats */}
          <div className="tb-stats">
            <div className="stat-card">
              <span className="stat-label">Total Accounts</span>
              <span className="stat-value">{trialBalanceData.accounts ? trialBalanceData.accounts.length : 0}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Total Debit</span>
              <span className="stat-value debit-color">{formatCurrency(trialBalanceData.totals.total_debit)}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Total Credit</span>
              <span className="stat-value credit-color">{formatCurrency(trialBalanceData.totals.total_credit)}</span>
            </div>
            <div className="stat-card highlight">
              <span className="stat-label">Status</span>
              <span className={`stat-value ${trialBalanceData.totals.is_balanced ? 'balanced-text' : 'unbalanced-text'}`}>
                {trialBalanceData.totals.is_balanced ? '✅ Balanced' : '❌ Unbalanced'}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="tb-no-data">
          <span className="no-data-icon">📭</span>
          <p>No data available. Please generate the report.</p>
        </div>
      )}
    </div>
  );
};

export default TrialBalance;